import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Appreciations } from "@/components/Appreciations";

export const REACTIONS = [
  { id: "like", emoji: "👍", label: "Like" },
  { id: "mind_blown", emoji: "🤯", label: "Mind blown" },
  { id: "concerning", emoji: "😟", label: "Concerning" },
  { id: "important", emoji: "❗", label: "Important" },
  { id: "hopeful", emoji: "🌱", label: "Hopeful" },
  { id: "sad", emoji: "😔", label: "Sad" },
];
export const REACTION = Object.fromEntries(REACTIONS.map((r) => [r.id, r]));

const Picker = ({ onPick, testid }) => (
  <div role="menu" className="absolute bottom-full left-0 z-30 mb-2 flex gap-0.5 rounded-full border border-border bg-white px-1.5 py-1 shadow-lg fade-up" data-testid={`${testid}-picker`}>
    {REACTIONS.map((r) => (
      <button key={r.id} type="button" role="menuitem" aria-label={r.label} title={r.label} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPick(r.id); }}
        className="grid h-10 w-10 place-items-center rounded-full text-2xl transition-transform duration-150 hover:-translate-y-1 hover:scale-125" data-testid={`${testid}-pick-${r.id}`}>{r.emoji}</button>
    ))}
  </div>
);

// One tap = Like (or remove your reaction); press and hold on phones, or hover on computers, to pick another.
const ReactionButton = ({ mine, onReact, testid, small }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const held = useRef(false);
  const later = (fn, ms) => { clearTimeout(timer.current); timer.current = setTimeout(fn, ms); };
  const r = mine && REACTION[mine];
  const pick = (id) => { setOpen(false); onReact(id); };
  return (
    <div className="relative" onMouseEnter={() => later(() => setOpen(true), 450)} onMouseLeave={() => later(() => setOpen(false), 300)}>
      {open && <Picker onPick={pick} testid={testid} />}
      <button type="button" data-testid={`${testid}-btn`} aria-label={r ? `Your reaction: ${r.label}. Tap to remove` : "React. Tap to like, hold for more reactions"}
        onPointerDown={(e) => { if (e.pointerType !== "mouse") { held.current = false; later(() => { held.current = true; setOpen(true); }, 450); } }}
        onPointerUp={() => !held.current && clearTimeout(timer.current)} onContextMenu={(e) => e.preventDefault()} style={{ WebkitTouchCallout: "none" }}
        onClick={() => { if (held.current) { held.current = false; return; } setOpen(false); onReact(mine || "like"); }}
        className={`flex select-none items-center gap-1.5 rounded-full transition-colors hover:bg-black/5 ${small ? "px-2 py-0.5 text-xs" : "px-3 py-1.5"} ${r ? "font-medium text-[hsl(var(--social))]" : ""}`}>
        <span className={`${small ? "text-xs" : "text-base"} leading-none ${r ? "" : "opacity-60 grayscale"}`}>{(r || REACTION.like).emoji}</span>{r ? r.label : "React"}
      </button>
    </div>
  );
};

// The commonest reaction once it reaches 5, e.g. "12 found this important".
const SUMMARY_MIN = 5;
const PHRASE = { like: "liked this", mind_blown: "found this mind-blowing", concerning: "found this concerning", important: "found this important", hopeful: "found this hopeful", sad: "found this sad" };
export const ReactionSummary = ({ item, testid, className = "" }) => {
  const [k, n] = Object.entries(item.reaction_counts || {}).filter(([t]) => REACTION[t]).sort((a, b) => b[1] - a[1])[0] || [];
  if (!k || n < SUMMARY_MIN) return null;
  return <p className={`text-xs font-medium text-slate-600 ${className}`} data-testid={`${testid}-summary`}><span className="mr-1">{REACTION[k].emoji}</span>{n} {PHRASE[k]}</p>;
};

// Why an older repost moved back up: "Ada commented · Max and 3 others reacted".
const part = ({ action, user, count }, meId) => `${user.user_id === meId ? "You" : user.name}${count > 1 ? ` and ${count - 1} other${count > 2 ? "s" : ""}` : ""} ${action}`;
export const BumpLine = ({ by, meId, testid, className = "" }) => (by?.length ? (
  <p className={`text-[11px] font-medium uppercase tracking-wider text-slate-400 ${className}`} data-testid={testid}>{by.map((b) => part(b, meId)).join(" · ")}</p>) : null);

// Reaction button + "top emojis and total" that opens who reacted.
export const ReactionBar = ({ item, setItem, url, whoUrl, testid, small = false }) => {
  const [who, setWho] = useState(false);
  const send = async (type) => {
    try {
      const { data } = await api.post(url, { type });
      setItem((x) => ({ ...x, my_reaction: data.my_reaction, liked: data.liked, reaction_count: data.reaction_count, reaction_counts: data.reaction_counts }));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const top = Object.entries(item.reaction_counts || {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([k]) => REACTION[k]).filter(Boolean);
  const n = item.reaction_count || 0;
  return (
    <>
      <ReactionButton mine={item.my_reaction} onReact={send} testid={testid} small={small} />
      {(!small || n > 0) && <button type="button" onClick={() => n > 0 && setWho(true)} disabled={!n} aria-label="See who reacted" data-testid={`${testid}-count`}
        className={`-ml-1 flex items-center gap-1 rounded-full transition-colors enabled:hover:bg-black/5 enabled:hover:underline ${small ? "px-1.5 py-0.5 text-xs" : "px-2 py-1.5"}`}>
        {top.length > 0 && <span className={`flex -space-x-1 leading-none ${small ? "text-xs" : "text-sm"}`}>{top.map((r) => <span key={r.id} title={r.label}>{r.emoji}</span>)}</span>}{n}
      </button>}
      {who && <Appreciations url={whoUrl} id={item.id} open={who} setOpen={setWho} />}
    </>
  );
};
